import { PlaceholderOption } from "@/components/rich-editor/rich-editor.types.ts";
import { VariableInsertMenu } from "@/components/variable-insert-menu/VariableInsertMenu.tsx";
import { Box, Input } from "@skailer-com/uikit";
import { ComponentPropsWithoutRef, forwardRef, useRef } from "react";
import { twMerge } from "tailwind-merge";

interface InputWithVariableInsertProps extends Omit<
  ComponentPropsWithoutRef<typeof Input>,
  "rightIcon"
> {
  variableOptions: PlaceholderOption[];
  disableSpacing?: boolean;
}

export const InputWithVariableInsert = forwardRef<HTMLInputElement, InputWithVariableInsertProps>(
  function InputWithVariableInsert(
    { className, onChange, onInput, onScroll, variableOptions, disableSpacing, ...inputProps },
    forwardedRef
  ) {
    const inputRef = useRef<HTMLInputElement | null>(null);

    const setInputRef = (node: HTMLInputElement | null) => {
      inputRef.current = node;

      if (typeof forwardedRef === "function") {
        forwardedRef(node);
        return;
      }

      if (forwardedRef) {
        forwardedRef.current = node;
      }
    };

    const insertVariable = (variable: string) => {
      const input = inputRef.current;
      if (!input) return;

      const currentValue = input.value ?? "";
      const selectionStart = input.selectionStart ?? currentValue.length;
      const selectionEnd = input.selectionEnd ?? currentValue.length;

      const beforeSelection = currentValue.slice(0, selectionStart);
      const spacer =
        !disableSpacing && beforeSelection.length > 0 && !/\s$/.test(beforeSelection) ? " " : "";
      const insertedText = disableSpacing ? variable : `${spacer}${variable} `;
      input.setRangeText(insertedText, selectionStart, selectionEnd, "end");
      input.dispatchEvent(new Event("input", { bubbles: true }));

      requestAnimationFrame(() => {
        input.focus();
      });
    };

    return (
      <Box className={twMerge("relative w-full", className)}>
        <Input
          {...inputProps}
          ref={setInputRef}
          autoComplete={inputProps.autoComplete ?? "new-password"}
          autoCorrect={inputProps.autoCorrect ?? "off"}
          autoCapitalize={inputProps.autoCapitalize ?? "off"}
          spellCheck={inputProps.spellCheck ?? false}
          onInput={onInput}
          onChange={onChange}
          onScroll={onScroll}
          className="[&_input]:pr-40"
        />
        <Box className="absolute top-7 right-0 -translate-y-1/2 z-10">
          <VariableInsertMenu options={variableOptions} onInsert={insertVariable} />
        </Box>
      </Box>
    );
  }
);
